import { useEffect, useMemo, useCallback, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Check, Download, HelpCircle, X } from "lucide-react";
import { formatCurrency, formatCurrencyWithCents } from "@/lib/currency";
import { usePaymentRuns, usePaymentRunInvoices, type PaymentRun } from "@/hooks/usePaymentRuns";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { computeSavingsCaptured } from "@/lib/paymentRunWeeks";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";

const runStatusColors: Record<PaymentRun["status"], string> = {
  draft: "bg-muted/60 text-muted-foreground border-border",
  processing: "bg-warning/10 text-warning border-warning/20",
  completed: "bg-primary/10 text-primary border-primary/20",
};

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  try {
    return format(parseISO(d), "MMM d, yyyy");
  } catch {
    return d;
  }
}

/* ---------- Week invoice type ---------- */
interface WeekInvoice {
  key: string;
  vendor_name: string;
  invoice_number: string | null;
  amount: number;
  early_pay_due_date: string;
  paid_date: string | null;
  potential_savings: number;
  status: "paid_early" | "not_paid_early" | "unknown";
}

const weekStatusBadge: Record<WeekInvoice["status"], string> = {
  paid_early: "bg-success/10 text-success border-success/20",
  not_paid_early: "bg-destructive/10 text-destructive border-destructive/20",
  unknown: "bg-muted/60 text-muted-foreground border-border",
};

const weekStatusLabel: Record<WeekInvoice["status"], string> = {
  paid_early: "Paid Early",
  not_paid_early: "Not Paid Early",
  unknown: "Unknown / Pending",
};

function exportRunAsCSV(run: PaymentRun, invoices: WeekInvoice[]) {
  const headers = ["Vendor", "Invoice #", "Amount", "Early Pay Due Date", "Paid Date", "Status"];
  const rows = invoices.map((inv) => [
    `"${inv.vendor_name.replace(/"/g, '""')}"`,
    `"${(inv.invoice_number ?? "").replace(/"/g, '""')}"`,
    inv.amount.toFixed(2),
    inv.early_pay_due_date ?? "",
    inv.paid_date ?? "",
    weekStatusLabel[inv.status],
  ]);
  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payment-run-${run.pay_date}-results.csv`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

interface PastPaymentRunViewProps {
  runId: string;
  allInvoices?: PARInvoiceRow[];
  onBack: () => void;
}

export function PastPaymentRunView({ runId, allInvoices = [], onBack }: PastPaymentRunViewProps) {
  const { getRun } = usePaymentRuns();
  const { toast } = useToast();
  const { invoices, fetchInvoices } = usePaymentRunInvoices(runId);

  const [run, setRun] = useState<PaymentRun | null>(null);

  useEffect(() => {
    getRun(runId).then(setRun);
  }, [runId, getRun]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  /* ---------- Build week invoices from allInvoices ---------- */
  const weekInvoices = useMemo((): WeekInvoice[] => {
    if (!run || allInvoices.length === 0) return [];
    const weekStart = run.pay_date;
    const weekEnd = format(addDays(parseISO(run.pay_date + "T00:00:00"), 6), "yyyy-MM-dd");

    const deduped = new Map<string, WeekInvoice>();
    for (const r of allInvoices) {
      if (!r.early_pay_due_date) continue;
      if (r.early_pay_due_date < weekStart || r.early_pay_due_date > weekEnd) continue;
      const key = `${r.vendor_name}|${r.invoice_number ?? ""}|${r.early_pay_due_date}`;
      const paidDate = r.paid_date || null;
      const status: WeekInvoice["status"] =
        paidDate && paidDate <= r.early_pay_due_date
          ? "paid_early"
          : paidDate
            ? "not_paid_early"
            : "unknown";
      const existing = deduped.get(key);
      const rank = (s: WeekInvoice["status"]) =>
        s === "paid_early" ? 2 : s === "not_paid_early" ? 1 : 0;
      if (!existing || rank(status) > rank(existing.status)) {
        deduped.set(key, {
          key,
          vendor_name: r.vendor_name,
          invoice_number: r.invoice_number ?? null,
          amount: r.amount_invoiced,
          early_pay_due_date: r.early_pay_due_date,
          paid_date: paidDate,
          potential_savings: r.potential_savings ?? 0,
          status,
        });
      }
    }
    return Array.from(deduped.values()).sort((a, b) => {
      const rank = (s: WeekInvoice["status"]) =>
        s === "paid_early" ? 0 : s === "not_paid_early" ? 1 : 2;
      if (rank(a.status) !== rank(b.status)) return rank(a.status) - rank(b.status);
      return b.amount - a.amount;
    });
  }, [run, allInvoices]);

  const paidEarly = useMemo(
    () => weekInvoices.filter((i) => i.status === "paid_early"),
    [weekInvoices]
  );
  const notPaidEarly = useMemo(
    () => weekInvoices.filter((i) => i.status === "not_paid_early"),
    [weekInvoices]
  );
  const unknownPending = useMemo(
    () => weekInvoices.filter((i) => i.status === "unknown"),
    [weekInvoices]
  );

  const { capturedSavings, totalPotentialSavings, capturedPct } = useMemo(
    () =>
      computeSavingsCaptured(
        weekInvoices.map((i) => ({
          potential_savings: i.potential_savings,
          paid_early: i.status === "paid_early",
        }))
      ),
    [weekInvoices]
  );

  const handleExport = useCallback(() => {
    if (!run) return;
    exportRunAsCSV(run, weekInvoices.length > 0 ? weekInvoices : []);
    toast({ description: "CSV exported", duration: 1500 });
  }, [run, weekInvoices, toast]);

  if (!run) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        Loading run…
      </div>
    );
  }

  /* Fallback when no PAR invoice data is loaded */
  const hasData = allInvoices.length > 0;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-start gap-3">
        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 text-xs -ml-2 shrink-0"
          onClick={onBack}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Payment Runs
        </Button>
      </div>

      {/* Run title + meta */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-bold leading-tight">{run.name}</h2>
          <Badge className={cn("text-[10px] border shrink-0", runStatusColors[run.status])}>
            {run.status}
          </Badge>
          <Badge className="text-[10px] border shrink-0 bg-muted/40 text-muted-foreground border-border/60">
            Past
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Pay date {fmtDate(run.pay_date)} · Budget {formatCurrency(run.budget)} · Created{" "}
          {fmtDate(run.created_at)}
        </p>
      </div>

      {hasData ? (
        <>
          {/* Status summary pills */}
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="flex items-center gap-1 rounded-full bg-success/10 text-success border border-success/20 px-2.5 py-1 font-semibold">
              <Check className="w-3 h-3" />
              {paidEarly.length} paid early ·{" "}
              {formatCurrency(paidEarly.reduce((s, i) => s + i.amount, 0))}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-destructive/10 text-destructive border border-destructive/20 px-2.5 py-1 font-semibold">
              <X className="w-3 h-3" />
              {notPaidEarly.length} not paid early ·{" "}
              {formatCurrency(notPaidEarly.reduce((s, i) => s + i.amount, 0))}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-muted/60 text-muted-foreground border border-border px-2.5 py-1 font-semibold">
              <HelpCircle className="w-3 h-3" />
              {unknownPending.length} unknown / pending ·{" "}
              {formatCurrency(unknownPending.reduce((s, i) => s + i.amount, 0))}
            </span>
          </div>

          {/* Metric cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-success/30 bg-success/5 px-4 py-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Savings Captured
              </p>
              <p className="text-xl font-bold text-success mt-0.5">
                {formatCurrencyWithCents(capturedSavings)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                of {formatCurrencyWithCents(totalPotentialSavings)} potential
              </p>
            </div>
            <div className="rounded-lg border px-4 py-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Savings Captured %
              </p>
              <p className="text-xl font-bold mt-0.5">
                {capturedPct !== null ? `${Math.round(capturedPct * 100)}%` : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                of all eligible invoices this week
              </p>
            </div>
            <div className="rounded-lg border px-4 py-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Total Invoices
              </p>
              <p className="text-xl font-bold mt-0.5">{weekInvoices.length}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {paidEarly.length} paid early
              </p>
            </div>
          </div>

          {/* Invoice table */}
          <Card className="overflow-hidden">
            <Table className="table-fixed">
              <colgroup>
                <col style={{ width: "24%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "20%" }} />
              </colgroup>
              <TableHeader className="sticky top-0 bg-card border-b border-border z-10">
                <TableRow className="[&_th]:py-1.5 [&_th]:px-2 [&_th]:text-left">
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Early Pay Due</TableHead>
                  <TableHead>Paid Date</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {weekInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-xs text-muted-foreground py-8"
                    >
                      No invoices with early pay deadline in this week's window
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {paidEarly.length > 0 && (
                      <>
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell
                            colSpan={6}
                            className="py-1 px-2 text-[10px] font-semibold text-success"
                          >
                            <Check className="w-3 h-3 inline-block mr-1" />
                            Paid Early ({paidEarly.length})
                          </TableCell>
                        </TableRow>
                        {paidEarly.map((inv) => (
                          <TableRow key={inv.key} className="[&_td]:py-1 [&_td]:px-2 bg-success/5">
                            <TableCell
                              className="text-[11px] font-medium truncate"
                              title={inv.vendor_name}
                            >
                              {inv.vendor_name}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground font-mono">
                              {inv.invoice_number || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] font-semibold text-right">
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {fmtDate(inv.early_pay_due_date)}
                            </TableCell>
                            <TableCell className="text-[10px] text-success font-medium">
                              {fmtDate(inv.paid_date)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                className={cn("text-[9px] border", weekStatusBadge[inv.status])}
                              >
                                {weekStatusLabel[inv.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}

                    {notPaidEarly.length > 0 && (
                      <>
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell
                            colSpan={6}
                            className="py-1 px-2 text-[10px] font-semibold text-destructive"
                          >
                            <X className="w-3 h-3 inline-block mr-1" />
                            Not Paid Early ({notPaidEarly.length})
                          </TableCell>
                        </TableRow>
                        {notPaidEarly.map((inv) => (
                          <TableRow key={inv.key} className="[&_td]:py-1 [&_td]:px-2 opacity-80">
                            <TableCell
                              className="text-[11px] font-medium truncate"
                              title={inv.vendor_name}
                            >
                              {inv.vendor_name}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground font-mono">
                              {inv.invoice_number || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] font-semibold text-right">
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {fmtDate(inv.early_pay_due_date)}
                            </TableCell>
                            <TableCell className="text-[10px] text-destructive font-medium">
                              {fmtDate(inv.paid_date)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                className={cn("text-[9px] border", weekStatusBadge[inv.status])}
                              >
                                {weekStatusLabel[inv.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}

                    {unknownPending.length > 0 && (
                      <>
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell
                            colSpan={6}
                            className="py-1 px-2 text-[10px] font-semibold text-muted-foreground"
                          >
                            <HelpCircle className="w-3 h-3 inline-block mr-1" />
                            Unknown / Pending ({unknownPending.length})
                          </TableCell>
                        </TableRow>
                        {unknownPending.map((inv) => (
                          <TableRow key={inv.key} className="[&_td]:py-1 [&_td]:px-2 opacity-60">
                            <TableCell
                              className="text-[11px] font-medium truncate"
                              title={inv.vendor_name}
                            >
                              {inv.vendor_name}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground font-mono">
                              {inv.invoice_number || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] font-semibold text-right">
                              {formatCurrency(inv.amount)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">
                              {fmtDate(inv.early_pay_due_date)}
                            </TableCell>
                            <TableCell className="text-[10px] text-muted-foreground">—</TableCell>
                            <TableCell className="text-right">
                              <Badge
                                className={cn("text-[9px] border", weekStatusBadge[inv.status])}
                              >
                                {weekStatusLabel[inv.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Footer actions */}
          <div className="flex items-center justify-between pt-1">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              onClick={handleExport}
              disabled={weekInvoices.length === 0}
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </Button>
          </div>
        </>
      ) : (
        /* No PAR data loaded — show the run invoices from the DB instead */
        <div className="border rounded-md overflow-hidden">
          <Table className="table-fixed">
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow className="[&_th]:py-1.5 [&_th]:px-2 [&_th]:text-[11px] [&_th]:font-semibold">
                <TableHead>Vendor</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Early Pay Due</TableHead>
                <TableHead className="text-right">Savings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-8">
                    No invoices in this run
                  </TableCell>
                </TableRow>
              ) : (
                invoices
                  .filter((i) => i.included)
                  .map((inv) => (
                    <TableRow key={inv.id} className="[&_td]:py-1 [&_td]:px-2">
                      <TableCell
                        className="text-[11px] font-medium truncate"
                        title={inv.vendor_name}
                      >
                        {inv.vendor_name}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground font-mono">
                        {inv.invoice_number || "—"}
                      </TableCell>
                      <TableCell className="text-[11px] font-semibold text-right">
                        {formatCurrency(inv.outstanding)}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">
                        {fmtDate(inv.early_pay_due_date)}
                      </TableCell>
                      <TableCell className="text-[11px] font-bold text-success text-right">
                        {formatCurrencyWithCents(inv.potential_savings)}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
